import { useEffect, useRef, useState } from "react";
import FootballAPI from "@api/football.api";

// intervals in milliseconds
const REFRESH_INTERVAL_LIVE = 10000; // 10s for live games
const REFRESH_INTERVAL_UPCOMING = 300000; // 5min for upcoming games

export default function useLiveFixturesRefresh(
  competitionId,
  initialFixtures = [],
  currentRoundId
) {
  const [fixtures, setFixtures] = useState(initialFixtures);
  const timeoutRef = useRef(null);

  const clearScheduled = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const scheduleNextRefresh = async (currentFixtures = fixtures) => {
    clearScheduled();
    const now = new Date();

    // flatten all fixtures across titles/rounds
    const allFixtures = currentFixtures.flatMap((f) => f.fixtures);

    // check if any fixture is live
    const hasLiveGame = allFixtures.some((f) => f.fixture.status.live === true);

    if (hasLiveGame) {
      // live polling every 30s
      timeoutRef.current = setTimeout(async () => {
        const updatedFixtures = await FootballAPI.getFixtures(
          competitionId,
          null,
          currentRoundId
        );
        setFixtures(updatedFixtures);
        scheduleNextRefresh(updatedFixtures);
      }, REFRESH_INTERVAL_LIVE);

      return;
    }

    // find the earliest upcoming fixture
    const upcomingFixtures = allFixtures
      .map((f) => new Date(f.fixture.date))
      .filter((startTime) => startTime > now);

    if (upcomingFixtures.length > 0) {
      const nextStartTime = upcomingFixtures.sort((a, b) => a - b)[0];
      const delay = nextStartTime - now + 10000; // 10s after start

      timeoutRef.current = setTimeout(async () => {
        const updatedFixtures = await FootballAPI.getFixtures(
          competitionId,
          null,
          currentRoundId
        );
        setFixtures(updatedFixtures);
        scheduleNextRefresh(updatedFixtures);
      }, Math.max(delay, REFRESH_INTERVAL_UPCOMING)); // fallback to max 5min
    }

    // if no live or upcoming games → do nothing
  };

  useEffect(() => {
    if (!currentRoundId) return;
    scheduleNextRefresh();
    return () => clearScheduled(); // cleanup on unmount
  }, [competitionId, currentRoundId]);

  return [fixtures, setFixtures];
}
